import UserPrepaidCardApi from '@apis/prepaid/UserPrepaidCardApi';
import Button from '@components/Button';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import setUserPrepaidCardList from '@hooks/prepaidCard/setUserPrepaidCardList';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const MAX_NAME_LENGTH = 20;

declare module '@stackflow/config' {
    interface Register {
        UpdateCardNameBottomSheet: {
            prepaidCardId: string;
            cardName: string;
        };
    }
}

/** 선불카드 이름 변경 */
const UpdateCardNameBottomSheet: ActivityComponentType<'UpdateCardNameBottomSheet'> = ({ params }) => {
    const { prepaidCardId, cardName } = params;
    const { t } = useTranslation('prepaidCard');
    const [inputName, setInputName] = useState(cardName);
    const { pop } = useFlow();
    const queryClient = useQueryClient();

    const handleChange = () => {
        const trimmed = inputName.trim();
        if (trimmed.length === 0) {
            toast.error(t('manage.updateName.emptyError'));
            return;
        }
        UserPrepaidCardApi.updatePrepaidCardName(prepaidCardId, { name: trimmed })
            .then(() => {
                pop();
                setUserPrepaidCardList(queryClient, (cards) => cards.map((card) => (
                    card.id === prepaidCardId ? { ...card, name: trimmed } : card
                )));
                toast.success(t('manage.updateName.success'));
            })
            .catch(() => {
                toast.error(t('manage.updateName.error'));
            });
    };

    return (
        <BottomSheet>
            <Header>
                <Text size='title' fontWeight='bold'>
                    {t('manage.updateName.modalTitle')}
                </Text>
            </Header>
            <Container fullWidth spacing={6}>
                <Column fullWidth spacing={2}>
                    <Text size='body-short' fontWeight='medium' color={colors.textSecondary}>
                        {t('manage.updateName.label')}
                    </Text>
                    <TextField value={inputName}
                        maxLength={MAX_NAME_LENGTH}
                        placeholder={t('manage.updateName.placeholder')}
                        helperText={t('manage.updateName.maxLength')}
                        onChange={(e) => setInputName(e.target.value)} />
                </Column>
                <Row fullWidth spacing={2}>
                    <Button variant='tone' fullWidth onClick={pop}>
                        {t('manage.updateName.cancel')}
                    </Button>
                    <Button fullWidth onClick={handleChange}>
                        {t('manage.updateName.confirm')}
                    </Button>
                </Row>
            </Container>
        </BottomSheet>
    );
};

const Header = styled.div`
    align-items: center;
    display: flex;
    height: 64px;
    justify-content: center;
`;

const Container = styled(Column)`
    padding: 0 var(--spacing-4) calc(var(--spacing-4) + var(--safe-area-bottom));
`;
export default UpdateCardNameBottomSheet;
